import { Component } from '@theme/component';
import {
  changeSelection,
  filterOptions,
  formatCount,
  geolocationErrorKey,
  groupOrder,
  matchStores,
  nearestStores,
  parseCoordinates,
  staggerDelay,
} from '@theme/eme-store-locator-core';

const EMPTY_SELECTION = { country: '', district: '', city: '' };
const OPTION_GROUPS = { country: 'countries', district: 'districts', city: 'cities' };
const ALL_LABELS = { country: 'allCountries', district: 'allDistricts', city: 'allCities' };
const ERROR_MESSAGES = { denied: 'errorDenied', unavailable: 'errorUnavailable' };
const MODE_MESSAGES = { nearby: 'nearStatus', closest: 'closestStatus' };
const POSITION_OPTIONS = { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 };

class EmeStoreLocator extends Component {
  requiredRefs = ['country', 'district', 'city', 'count', 'results', 'empty'];
  #stores = [];
  #indexOf = new Map();
  #groups = [];
  #selection = { ...EMPTY_SELECTION };
  #origin = null;
  #radiusKm = 0;
  #visible = new Set();
  #optionLists = new Map();
  #arrangement = '';
  #locating = false;

  connectedCallback() {
    super.connectedCallback();
    this.#stores = (this.refs.stores ?? []).map((element) => {
      const coordinates = parseCoordinates(element.dataset.lat, element.dataset.lng);
      return {
        element,
        country: element.dataset.country ?? '',
        district: element.dataset.district ?? '',
        city: element.dataset.city ?? '',
        group: element.dataset.group ?? '',
        lat: coordinates ? coordinates[0] : Number.NaN,
        lng: coordinates ? coordinates[1] : Number.NaN,
      };
    });
    this.#indexOf = new Map(this.#stores.map((store, index) => [store.element, index]));
    this.#groups = [...(this.refs.groups ?? [])].map((element) => ({
      element,
      key: element.dataset.group ?? '',
      list: element.querySelector('.eme-store-locator__list'),
      stores: this.#stores.filter((store) => element.contains(store.element)),
    }));
    this.#radiusKm = Number(this.dataset.radius) || 0;
    this.#selection = { ...EMPTY_SELECTION };
    this.#origin = null;
    this.#visible = new Set();
    this.#optionLists = new Map();
    this.#arrangement = '';
    this.#locating = false;
    if (this.refs.locate) this.refs.locate.hidden = !this.#canLocate();
    this.#render();
    this.dataset.ready = '';
  }

  select(event) {
    const { field } = event.target.dataset;
    if (!(field in OPTION_GROUPS)) return;
    this.#selection = changeSelection(this.#selection, field, event.target.value);
    this.#origin = null;
    this.#render();
  }

  clearFilters() {
    this.#reset();
    this.refs.country.focus();
  }

  showAll() {
    this.#reset();
    const { locate } = this.refs;
    if (locate && !locate.hidden) locate.focus();
    else this.refs.country.focus();
  }

  async locate() {
    const { locate } = this.refs;
    if (!locate || this.#locating || !this.#canLocate()) return;
    this.#locating = true;
    locate.setAttribute('aria-busy', 'true');
    locate.setAttribute('aria-disabled', 'true');
    this.#setStatus(this.dataset.locating ?? '');
    const selection = this.#selection;
    let errorMessage = '';
    try {
      const { coords } = await this.#getPosition();
      if (this.#selection === selection) {
        this.#origin = [coords.latitude, coords.longitude];
        this.#selection = { ...EMPTY_SELECTION };
      }
    } catch (error) {
      if (this.#selection === selection) {
        this.#origin = null;
        this.#selection = { ...EMPTY_SELECTION };
        errorMessage = this.dataset[ERROR_MESSAGES[geolocationErrorKey(error?.code)]] ?? '';
      }
    } finally {
      this.#locating = false;
      locate.removeAttribute('aria-busy');
      locate.removeAttribute('aria-disabled');
    }
    this.#render();
    if (errorMessage) this.#setStatus(errorMessage);
  }

  #reset() {
    this.#origin = null;
    this.#selection = { ...EMPTY_SELECTION };
    this.#render();
  }

  #canLocate() {
    return Boolean(this.refs.locate) && 'geolocation' in navigator && window.isSecureContext;
  }

  #getPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, POSITION_OPTIONS);
    });
  }

  #setStatus(message) {
    const { status } = this.refs;
    if (status && status.textContent !== message) status.textContent = message;
  }

  #render() {
    this.#renderOptions();
    const { mode, indices } = this.#origin
      ? nearestStores(this.#stores, this.#origin, this.#radiusKm)
      : matchStores(this.#stores, this.#selection);
    const located = mode === 'nearby' || mode === 'closest';
    const order = located ? groupOrder(this.#stores, indices) : [];
    this.#arrange(located ? indices : null, order);
    const visible = new Set(indices);
    this.#renderCards(visible);
    this.#renderGroups(order[0]);
    this.#renderCount(visible.size, located);
    this.#renderEmpty(visible.size, located);
    this.#renderActions(mode);
    if (!this.#locating) this.#setStatus(visible.size ? this.dataset[MODE_MESSAGES[mode]] ?? '' : '');
    this.#visible = visible;
  }

  #renderOptions() {
    const options = filterOptions(this.#stores, this.#selection);
    for (const [field, group] of Object.entries(OPTION_GROUPS)) {
      const select = this.refs[field];
      const values = options[group];
      const signature = values.join('\n');
      if (this.#optionLists.get(field) !== signature) {
        const all = new Option(this.dataset[ALL_LABELS[field]] ?? '', '');
        select.replaceChildren(all, ...values.map((value) => new Option(value, value)));
        this.#optionLists.set(field, signature);
      }
      select.value = this.#selection[field];
    }
  }

  #arrange(ranked, order) {
    const signature = ranked ? ranked.join(',') : '';
    if (signature === this.#arrangement) return;
    this.#arrangement = signature;
    const rank = new Map((ranked ?? []).map((index, position) => [this.#stores[index], position]));
    const byRank = (a, b) => (rank.get(a) ?? Infinity) - (rank.get(b) ?? Infinity) || this.#indexOf.get(a.element) - this.#indexOf.get(b.element);
    const groupRank = new Map(order.map((key, position) => [key, position]));
    const groups = [...this.#groups].sort(
      (a, b) => (groupRank.get(a.key) ?? Infinity) - (groupRank.get(b.key) ?? Infinity) || this.#groups.indexOf(a) - this.#groups.indexOf(b)
    );
    for (const group of groups) {
      group.list?.append(...[...group.stores].sort(byRank).map((store) => store.element));
      this.refs.results.append(group.element);
    }
  }

  #renderCards(visible) {
    const entering = [];
    for (const group of this.#groups) {
      let position = 0;
      for (const element of group.list?.children ?? []) {
        const index = this.#indexOf.get(element);
        if (index === undefined) continue;
        const show = visible.has(index);
        element.hidden = !show;
        if (!show) continue;
        if (!this.#visible.has(index)) {
          element.style.setProperty('--eme-store-delay', `${staggerDelay(position)}ms`);
          element.classList.remove('eme-store-locator__card--enter');
          entering.push(element);
        }
        position += 1;
      }
    }
    if (!entering.length) return;
    void this.offsetWidth;
    for (const element of entering) element.classList.add('eme-store-locator__card--enter');
  }

  #renderGroups(nearest) {
    for (const group of this.#groups) {
      group.element.hidden = !group.stores.some((store) => !store.element.hidden);
      group.element.classList.toggle('eme-store-locator__group--near', group.key === nearest);
    }
  }

  #renderCount(count, located) {
    const base = formatCount(count, {
      one: this.dataset.countOne ?? '%n',
      other: this.dataset.countOther ?? '%n',
    });
    const text = located && this.dataset.nearYou ? `${base} ${this.dataset.nearYou}` : base;
    if (this.refs.count.textContent !== text) this.refs.count.textContent = text;
  }

  #renderEmpty(count, located) {
    const { empty, results } = this.refs;
    results.hidden = count === 0;
    empty.hidden = count !== 0;
    if (count !== 0) return;
    const message = located ? this.dataset.emptyNearby : this.dataset.emptyFiltered;
    if (empty.textContent !== message) empty.textContent = message ?? '';
  }

  #renderActions(mode) {
    const { clear, showAll } = this.refs;
    if (clear) clear.hidden = mode !== 'filtered';
    if (showAll) showAll.hidden = !(mode === 'nearby' || mode === 'closest');
  }
}

if (!customElements.get('eme-store-locator')) customElements.define('eme-store-locator', EmeStoreLocator);
